import axios from 'axios';
import throttle from 'just-throttle';
import { FILTER_EVENT } from '../../components/filters/FilterEvent';
import SearchPageFilterManager from '../../components/filters/SearchPageFilterManager';
import { SEARCH_FILTERS_CLEARED, SEARCH_FILTERS_SUBMITTED } from '../../events/data-layer-events';
import { SEARCH_RESULT_SUCCESS } from '../../events/search-events';
import { pushTrackingEventToDataLayer } from '../../helpers/data-layer-helper';
import Page from '../page';
import { POPIN_EVENT } from './popinEvent';
import PopinManager from './popinManager';

export default class GenericSearch extends Page {
  constructor(...args) {
    super(...args);

    this._opacified = false;

    this._retrieveDOM();
    this._searchRoute = 'place_search_payload';
    this._getSearchRoute();

    this.FilterManagerBar = new SearchPageFilterManager(this.dom.page);
    this.PopinManager = new PopinManager(this._device);

    this._bind();
  }

  _getSearchRoute() {
    const route = this.dom.page.getAttribute('data-search-route');
    if (route) {
      this._searchRoute = route;
    }
  }

  _retrieveDOM() {
    this.dom.body = document.body;
    this.dom.filtersContainerBar = this.dom.page.querySelector(
      '.js-filters-container-bar',
    );
    this.dom.submitFiltersBar = this.dom.page.querySelectorAll(
      '.js-submit-filters-bar',
    );
    this.dom.removeFilters = this.dom.page.querySelectorAll(
      '.js-remove-filters',
    );

    this.dom.mobileFiltersButton = this.dom.page.querySelector(
      '.js-mobile-filters',
    );

    // Results
    this.dom.results = this.dom.page.querySelector('.js-results');
    this.dom.breadcrumb = this.dom.page.querySelector('.js-breadcrumb');
    this.dom.pagination = this.dom.page.querySelector('.js-pagination');
    this.dom.hits = this.dom.page.querySelector('.js-hits-number');
    this.dom.title = this.dom.page.querySelector('.js-title');
  }

  _bind() {
    // AutoSubmit
    this.FilterManagerBar.on(
      FILTER_EVENT.AUTO_SUBMIT,
      this._retrieveValuesFiltersBar.bind(this),
    );

    // FILTER MANAGER
    this.FilterManagerBar.on(FILTER_EVENT.OPENED, (Filter) => {
      if (Filter.getPreventOpacify()) {
        return;
      }
      this._manageOpacify('FILTER_EVENT.OPENED');
    });
    this.FilterManagerBar.on(FILTER_EVENT.CLOSED, () => {
      this._manageOpacify('FILTER_EVENT.CLOSED');
    });
    this.FilterManagerBar.on(FILTER_EVENT.DISPLAY_VALUE, (Filter) => {
      this.dom.page.querySelectorAll(Filter._displayValue).forEach((el) => {
        el.innerHTML = Filter.getValues()[0];
      });
    });

    // POPIN MANAGER
    this.PopinManager.on(POPIN_EVENT.OPENED, () => {
      this._manageOpacify('POPIN_EVENT.OPENED');
    });
    this.PopinManager.on(POPIN_EVENT.CLOSED, () => {
      this._manageOpacify('POPIN_EVENT.CLOSED');
      this.PopinManager.updateAllCounters(this.FilterManagerBar.getNormalizedValues());
    });

    // Retrieve all values from filters
    for (let i = 0; i < this.dom.submitFiltersBar.length; i++) {
      const el = this.dom.submitFiltersBar[i];
      el.addEventListener('click', () => {
        this._submitFiltersButtonClicked();
      });
    }

    for (let i = 0; i < this.dom.removeFilters.length; i++) {
      const el = this.dom.removeFilters[i];
      const _arrayFilters = el.getAttribute('data-filters').split(',');
      el.addEventListener('click', () => {
        this._clearFiltersButtonClicked(_arrayFilters);
      });
    }

    // Body
    document.body.addEventListener('click', (e) => {
      if (this.FilterManagerBar.isOpened() || this.PopinManager.isOpened()) {
        if (
          e.target.closest('.js-filters-container-bar') !== null ||
          e.target.closest('.ct--filter') !== null ||
          e.target.closest('.js-remove-choice-response') !== null // for multi select choice response
        ) {
          return;
        }
        if (this.FilterManagerBar.isOpened()) {
          this.FilterManagerBar.closeAll();
        }
        if (this.PopinManager.isOpened()) {
          this.PopinManager.closeAll();
          this.PopinManager.emit(POPIN_EVENT.CLOSED);
        }
        this._manageOpacify();
      }
    });

    // tries to refresh state on page load
    this.FilterManagerBar._changedHandler();

    // tries to refresh counters on page load
    this.PopinManager.updateAllCounters(this.FilterManagerBar.getNormalizedValues());

    // initialize selection manager
    this.selectionManager.init();

    if (this._device === 'mobile' && this.dom.mobileFiltersButton) {
      window.addEventListener('scroll', throttle(this._searchScrollHandler.bind(this), 600));
    }

    this.selectionManager.setSearchContext(this.FilterManagerBar.getNormalizedValues());
  }

  _submitFiltersButtonClicked() {
    this._retrieveValuesFiltersBar();
    pushTrackingEventToDataLayer(SEARCH_FILTERS_SUBMITTED);
  }

  _clearFiltersButtonClicked(filters) {
    this.FilterManagerBar.clearTargets(filters);
    this._retrieveValuesFiltersBar();
    pushTrackingEventToDataLayer(SEARCH_FILTERS_CLEARED);
  }

  _searchScrollHandler() {
    if (this.getScroll() > 100) {
      this.dom.mobileFiltersButton.classList.add('show');
    } else {
      this.dom.mobileFiltersButton.classList.remove('show');
    }
  }

  getScroll() {
    return (
      (window.pageYOffset || document.documentElement.scrollTop) -
      (document.documentElement.clientTop || 0)
    );
  }

  _manageOpacify() {
    if (
      this.FilterManagerBar.isOpened() === false &&
      this.PopinManager.isOpened() === false
    ) {
      this._unsetOpacify();
    } else {
      this._setOpacify();
    }
  }

  _enableNoScroll() {
    if (this._device === 'mobile') {
      this.dom.body.classList.add('no-scroll');
    }
  }

  _disableNoScroll() {
    if (this._device === 'mobile') {
      this.dom.body.classList.remove('no-scroll');
    }
  }

  _setOpacify() {
    this._opacified = true;
    // hack for ios
    if (navigator.userAgent.match(/iPhone|iPad|iPod/i) !== null) {
      document.body.classList.add('platform-ios');
      document.body.style.top = -window.scrollY + 'px';
    }
    this._enableNoScroll();
    this.dom.body.classList.add('modal-open');
    this.dom.page.classList.add('opacify');
  }

  _unsetOpacify() {
    this._opacified = false;
    this._disableNoScroll();
    this.dom.body.classList.remove('modal-open');
    this.dom.page.classList.remove('opacify');
    // hack for ios
    if (document.body.classList.contains('platform-ios') === true) {
      document.body.classList.remove('platform-ios');
      window.scrollTo(0, -parseInt(document.body.style.top, 10));
      document.body.style.top = '';
    }
  }

  _retrieveValuesFiltersBar() {
    this.PopinManager.updateAllCounters(this.FilterManagerBar.getNormalizedValues());
    this.FilterManagerBar.closeAll();
    this.PopinManager.closeAll();
    this._manageOpacify();
    const values = this.FilterManagerBar.getValues();
    this._request(values);
  }

  _request(values) {
    axios.post(this._searchRoute, this.FilterManagerBar.getNormalizedValues()).then((response) => {
      this._success(response.data);
    });
  }

  _success(xhr) {
    // Update innerHTML
    this.dom.breadcrumb.innerHTML = xhr.breadcrumb.html;
    this.dom.pagination.innerHTML = xhr.pagination.html;
    this.dom.hits.innerHTML = xhr.pagination.total_hits;
    if (this.dom.title && xhr.title) {
      this.dom.title.innerHTML = xhr.title;
    }

    // Remove current item binds
    if (this._hasSearchManager) {
      this.SearchManager.unbind();
    }
    if (xhr.has_results) {
      // Format HTML
      let html = '';
      for (let i = 0, j = xhr.results.length; i < j; i++) {
        // insert "custom" result before 3rd result - if exists
        if (i === 2 && xhr.extra_result) {
          html += xhr.extra_result;
        }
        const result = xhr.results[i];
        html += result.html;
      }

      // Replace HTML
      this.dom.results.innerHTML = html;
    } else if (xhr.no_results_html) {
      // Replace with custom HTML
      this.dom.results.innerHTML = xhr.no_results_html;
    } else {
      // Replace HTML
      this.dom.results.innerHTML =
        '<div class="no-results"><p>Aucun résultat n\'a été trouvé pour ces critères de recherche.</p></div>';
    }

    // Update URL
    if (xhr.hasOwnProperty('url')) {
      window.history.pushState({}, null, xhr.url);
    }

    // Rebind events
    if (this._hasSearchManager) {
      this.SearchManager.rebind(this.dom.page);
      this.SearchManager.scrollToTop();
    }

    // refresh selection manager
    this.selectionManager.setSearchContext(this.FilterManagerBar.getNormalizedValues());
    this.selectionManager.handleUI();

    this.dispatcher.emit(SEARCH_RESULT_SUCCESS);
  }
}
